import { type Key, useCallback, useEffect, useMemo, useRef, useState } from "react";

import { useLazyQuery, useQuery } from "@apollo/client";
import { type TreeSelectNode } from "@snapchat/snap-design-system";
import { useIntl } from "react-intl";

import {
    type InputMaybe,
    type InsightsDemographicSpecJson,
    type InsightsDeviceSpecJson,
    type InsightsGeoSpecJson,
    InsightsOsType,
    type InsightsTargetingSpecJson,
    SpecSource,
} from "../../gql-generated/insights-types";
import { debounce } from "../../shared/utils/event";
import { InsightsApolloClient } from "../api";
import {
    type GetAudiencePlannerQuery,
    type GetAudiencePlannerQueryVariables,
    type GetLensMetadataQuery,
    type GetLensMetadataQueryVariables,
    getAudiencePlannerQuery,
    getLensesMetadataQuery,
} from "../api/queries";
import { messages } from "../components/messages.generated";
import { type InsightsParsedData } from "../types";
import {
    countryPrefix,
    emptyTargetingSpec,
    findExistingSpecByOsType,
    getAllCountries,
    getAllDevices,
    getAllLanguages,
    getAllOS,
    languagePrefix,
    osPrefix,
    parseDemographicOverviewData,
} from "../utils";

export const useFilter = (): {
    isLoading: boolean;
    filterTree: TreeSelectNode[];
    data: GetAudiencePlannerQuery | undefined;
    parsedInsightsData: InsightsParsedData | null;
    lensMetadata: GetLensMetadataQuery | undefined;
    lensMetadataLoading: boolean;
    onFilter: (checkedKeys: Key[], triggeringKey: Key) => void;
    onReset: () => void;
} => {
    const client = InsightsApolloClient();
    const [targetingSpec, setTargetingSpec] = useState<InsightsTargetingSpecJson>({ ...emptyTargetingSpec });
    const [filterTree, setFilterTree] = useState<TreeSelectNode[]>([]);
    const [parsedInsightsData, setParsedInsightsData] = useState<InsightsParsedData | null>(null);
    const [devices, setDevices] = useState<TreeSelectNode[]>([]);
    const [countries, setCountries] = useState<TreeSelectNode[]>([]);
    const [osTypes, setOSTypes] = useState<TreeSelectNode[]>([]);
    const [languages, setLanguages] = useState<TreeSelectNode[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const isFirstLoad = useRef(true);
    const intl = useIntl();

    const [loadAudienceData, { loading, data }] = useLazyQuery<
        GetAudiencePlannerQuery,
        GetAudiencePlannerQueryVariables
    >(getAudiencePlannerQuery, {
        fetchPolicy: "no-cache",
        variables: {
            targetingSpec: targetingSpec,
            specSource: SpecSource.AudienceLens,
            fieldFilter: [],
        },
        client,
    });

    const debouncedLoadAudienceData = debounce(loadAudienceData, 300);

    const { data: lensMetadata, loading: lensMetadataLoading } = useQuery<
        GetLensMetadataQuery,
        GetLensMetadataQueryVariables
    >(getLensesMetadataQuery, {
        variables: {
            ids: parsedInsightsData ? parsedInsightsData.lensStats.map((stat) => stat.id) : [],
        },
        client,
    });

    useEffect(() => {
        if (isFirstLoad.current) {
            void loadAudienceData();
            isFirstLoad.current = false;
        }
    }, [loadAudienceData]);

    useEffect(() => {
        if (!loading && data) {
            setParsedInsightsData(parseDemographicOverviewData(data));
            setIsLoading(false);
            if (filterTree.length === 0) {
                // only get filters on first query with empty targeting spec to get all possible values
                const devices = getAllDevices(data);
                const countries = getAllCountries(data);
                const osTypes = getAllOS(data);
                const languages = getAllLanguages(data);
                setDevices(devices);
                setCountries(countries);
                setOSTypes(osTypes);
                setLanguages(languages);
                setFilterTree([
                    {
                        key: intl.formatMessage(messages.deviceManufacturer),
                        value: intl.formatMessage(messages.deviceManufacturer),
                        title: intl.formatMessage(messages.deviceManufacturer),
                        checkable: false,
                        children: devices,
                    },
                    {
                        key: intl.formatMessage(messages.language),
                        value: intl.formatMessage(messages.language),
                        title: intl.formatMessage(messages.language),
                        checkable: false,
                        children: languages,
                    },
                    {
                        key: intl.formatMessage(messages.country),
                        value: intl.formatMessage(messages.country),
                        title: intl.formatMessage(messages.country),
                        checkable: false,
                        children: countries,
                    },
                    {
                        key: intl.formatMessage(messages.operatingSystem),
                        value: intl.formatMessage(messages.operatingSystem),
                        title: intl.formatMessage(messages.operatingSystem),
                        checkable: false,
                        children: osTypes,
                    },
                ]);
            }
        }
    }, [loading, data, filterTree, setTargetingSpec, intl]);

    const deviceMakers = Object.values(devices).map((device) => device.key);
    const deviceKeys = useMemo(() => {
        const keys = [...deviceMakers];
        devices.forEach((device: TreeSelectNode) => {
            device.children?.forEach((child) => {
                keys.push(child.key);
            });
        });
        return keys;
    }, [devices, deviceMakers]);
    const languageKeys = Object.values(languages).map((language) => language.key);
    const countryKeys = Object.values(countries).map((country) => country.key);
    const osKeys = Object.values(osTypes).map((osType) => osType.key);

    const handleDeviceMakers = useCallback(
        (
            newTargetingSpec: InsightsTargetingSpecJson,
            triggeringKey: Key,
            checkedKeys: Key[],
        ): InsightsTargetingSpecJson => {
            // Check box
            if (checkedKeys.includes(triggeringKey)) {
                // Selecting all devices under Apple
                if (triggeringKey.toString() === "Apple") {
                    const existingSpec = findExistingSpecByOsType(newTargetingSpec, InsightsOsType.IOs);
                    const marketingNames: string[] =
                        devices
                            .find((device) => device.key === triggeringKey.toString())
                            ?.children?.map((child) => child.key) ?? [];
                    if (existingSpec) {
                        (existingSpec.marketing_name as InputMaybe<string>[]) = [
                            ...(existingSpec.marketing_name ?? []),
                            ...marketingNames,
                        ];
                    } else {
                        (newTargetingSpec.devices as InsightsDeviceSpecJson[]).push({
                            os_type: InsightsOsType.IOs,
                            marketing_name: marketingNames,
                        });
                    }
                    // Selecting all devices under non-Apple
                } else {
                    const existingSpec = findExistingSpecByOsType(newTargetingSpec, InsightsOsType.Android);
                    const marketingNames: string[] =
                        devices
                            .find((device) => device.key === triggeringKey.toString())
                            ?.children?.map((child) => child.key) ?? [];
                    if (existingSpec) {
                        (existingSpec.marketing_name as InputMaybe<string>[]) = [
                            ...(existingSpec.marketing_name ?? []),
                            ...marketingNames,
                        ];
                    } else {
                        (newTargetingSpec.devices as InsightsDeviceSpecJson[]).push({
                            os_type: InsightsOsType.Android,
                            marketing_name: marketingNames,
                        });
                    }
                }
            } else {
                // Uncheck box
                if (triggeringKey.toString() === "Apple") {
                    (newTargetingSpec.devices as InputMaybe<InsightsDeviceSpecJson>[]) =
                        newTargetingSpec.devices?.filter((spec) => spec?.os_type !== InsightsOsType.IOs) ?? [];
                } else {
                    const existingSpec = findExistingSpecByOsType(newTargetingSpec, InsightsOsType.Android);
                    if (existingSpec) {
                        (existingSpec.marketing_name as InputMaybe<string>[]) =
                            existingSpec.marketing_name?.filter(
                                (name) => !name?.toUpperCase().includes(triggeringKey.toString().toUpperCase()),
                            ) ?? [];
                        if (existingSpec.marketing_name?.length === 0) {
                            (newTargetingSpec.devices as InputMaybe<InsightsDeviceSpecJson>[]) =
                                newTargetingSpec.devices?.filter((spec) => spec !== existingSpec) ?? [];
                        }
                    }
                }
            }
            setTargetingSpec(newTargetingSpec);
            return newTargetingSpec;
        },
        [devices],
    );

    const handleDeviceKeys = useCallback(
        (
            newTargetingSpec: InsightsTargetingSpecJson,
            triggeringKey: Key,
            checkedKeys: Key[],
        ): InsightsTargetingSpecJson => {
            // Check box
            if (checkedKeys.includes(triggeringKey)) {
                // Selecting a specific apple device
                if (triggeringKey.toString().toLowerCase().includes("apple")) {
                    const existingSpec = findExistingSpecByOsType(newTargetingSpec, InsightsOsType.IOs);
                    if (existingSpec) {
                        (existingSpec.marketing_name as string[]).push(triggeringKey.toString());
                    } else {
                        (newTargetingSpec.devices as InsightsDeviceSpecJson[]).push({
                            os_type: InsightsOsType.IOs,
                            marketing_name: [triggeringKey.toString()],
                        });
                    }
                    // Selecting a specific android device
                } else {
                    const existingSpec = findExistingSpecByOsType(newTargetingSpec, InsightsOsType.Android);
                    if (existingSpec) {
                        (existingSpec.marketing_name as string[]).push(triggeringKey.toString());
                    } else {
                        (newTargetingSpec.devices as InsightsDeviceSpecJson[]).push({
                            os_type: InsightsOsType.Android,
                            marketing_name: [triggeringKey.toString()],
                        });
                    }
                }
            } else {
                // Uncheck box
                let osType = InsightsOsType.Android;
                if (triggeringKey.toString().toLowerCase().includes("apple")) {
                    osType = InsightsOsType.IOs;
                }
                const existingSpec = findExistingSpecByOsType(newTargetingSpec, osType);
                if (existingSpec) {
                    (existingSpec.marketing_name as InputMaybe<string>[]) =
                        existingSpec.marketing_name?.filter((name) => name !== triggeringKey.toString()) ?? [];
                    if (existingSpec.marketing_name?.length === 0) {
                        (newTargetingSpec.devices as InputMaybe<InsightsDeviceSpecJson>[]) =
                            newTargetingSpec.devices?.filter((spec) => spec !== existingSpec) ?? [];
                    }
                }
            }
            setTargetingSpec(newTargetingSpec);
            return newTargetingSpec;
        },
        [],
    );

    const handleLanguage = useCallback(
        (
            newTargetingSpec: InsightsTargetingSpecJson,
            triggeringKey: Key,
            checkedKeys: Key[],
        ): InsightsTargetingSpecJson => {
            // Check box
            if (checkedKeys.includes(triggeringKey)) {
                if (newTargetingSpec.demographics?.length === 0) {
                    (newTargetingSpec.demographics as InsightsDemographicSpecJson[]) = [
                        { languages: [triggeringKey.toString().replace(languagePrefix, "")] },
                    ];
                } else {
                    (newTargetingSpec.demographics?.[0]?.languages as InputMaybe<string>[]).push(
                        triggeringKey.toString().replace(languagePrefix, ""),
                    );
                }
            } else {
                // Uncheck box
                const demographic = newTargetingSpec.demographics?.[0];
                if (demographic) {
                    (demographic.languages as InputMaybe<string>[]) =
                        demographic.languages?.filter(
                            (language) => language !== triggeringKey.toString().replace(languagePrefix, ""),
                        ) ?? [];
                }
            }
            setTargetingSpec(newTargetingSpec);
            return newTargetingSpec;
        },
        [],
    );

    const handleCountry = useCallback(
        (
            newTargetingSpec: InsightsTargetingSpecJson,
            triggeringKey: Key,
            checkedKeys: Key[],
        ): InsightsTargetingSpecJson => {
            // Check box
            if (checkedKeys.includes(triggeringKey)) {
                if (newTargetingSpec.geos?.length === 0) {
                    (newTargetingSpec.geos as InsightsGeoSpecJson[]) = [
                        { country_code: triggeringKey.toString().replace(countryPrefix, "") },
                    ];
                } else {
                    (newTargetingSpec.geos as InsightsGeoSpecJson[]).push({
                        country_code: triggeringKey.toString().replace(countryPrefix, ""),
                    });
                }
            } else {
                // Uncheck box
                (newTargetingSpec.geos as InputMaybe<InsightsGeoSpecJson>[]) =
                    newTargetingSpec.geos?.filter(
                        (country) => country?.country_code !== triggeringKey.toString().replace(countryPrefix, ""),
                    ) ?? [];
            }
            setTargetingSpec(newTargetingSpec);
            return newTargetingSpec;
        },
        [],
    );

    const handleOS = useCallback(
        (
            newTargetingSpec: InsightsTargetingSpecJson,
            triggeringKey: Key,
            checkedKeys: Key[],
        ): InsightsTargetingSpecJson => {
            const osType =
                triggeringKey.toString().toLowerCase().replace(osPrefix, "") === "ios"
                    ? InsightsOsType.IOs
                    : InsightsOsType.Android;
            // Check box
            if (checkedKeys.includes(triggeringKey)) {
                (newTargetingSpec.devices as InsightsDeviceSpecJson[]).push({ os_type: osType });
            } else {
                // Uncheck box
                (newTargetingSpec.devices as InputMaybe<InsightsDeviceSpecJson>[]) =
                    newTargetingSpec.devices?.filter((spec) => spec?.os_type?.toLowerCase() !== osType.toLowerCase()) ??
                    [];
            }
            setTargetingSpec(newTargetingSpec);
            return newTargetingSpec;
        },
        [],
    );

    const onFilter = useCallback(
        (checkedKeys: Key[], triggeringKey: Key): void => {
            let newTargetingSpec: InsightsTargetingSpecJson = {
                ...targetingSpec,
            };
            if (deviceMakers.includes(triggeringKey.toString())) {
                newTargetingSpec = handleDeviceMakers(newTargetingSpec, triggeringKey, checkedKeys);
            } else if (deviceKeys.includes(triggeringKey.toString())) {
                newTargetingSpec = handleDeviceKeys(newTargetingSpec, triggeringKey, checkedKeys);
            } else if (languageKeys.includes(triggeringKey.toString())) {
                newTargetingSpec = handleLanguage(newTargetingSpec, triggeringKey, checkedKeys);
            } else if (countryKeys.includes(triggeringKey.toString())) {
                newTargetingSpec = handleCountry(newTargetingSpec, triggeringKey, checkedKeys);
            } else if (osKeys.includes(triggeringKey.toString())) {
                newTargetingSpec = handleOS(newTargetingSpec, triggeringKey, checkedKeys);
            } else {
                return;
            }
            debouncedLoadAudienceData({
                variables: {
                    targetingSpec: newTargetingSpec,
                    specSource: SpecSource.AudienceLens,
                    fieldFilter: [],
                },
            });
        },
        [
            debouncedLoadAudienceData,
            targetingSpec,
            deviceMakers,
            deviceKeys,
            languageKeys,
            countryKeys,
            osKeys,
            handleDeviceMakers,
            handleDeviceKeys,
            handleLanguage,
            handleCountry,
            handleOS,
        ],
    );

    const onReset = useCallback(() => {
        setTargetingSpec(emptyTargetingSpec);
        debouncedLoadAudienceData({
            variables: {
                targetingSpec: emptyTargetingSpec,
                specSource: SpecSource.AudienceLens,
                fieldFilter: [],
            },
        });
    }, [setTargetingSpec, debouncedLoadAudienceData]);

    return {
        isLoading: isLoading,
        filterTree: filterTree,
        data: data,
        parsedInsightsData: parsedInsightsData,
        lensMetadata: lensMetadata,
        lensMetadataLoading: lensMetadataLoading,
        onFilter: onFilter,
        onReset: onReset,
    };
};
